import { OPEN_CASH_DRAWER_TEMPLATE_TYPE, type PrinterTemplate } from '../types';

const ESC = 0x1b;
const GS = 0x1d;

export interface EscPosEncodeOptions {
  openCashDrawer?: boolean;
  addFinalCut?: boolean;
  codePage?: 'utf8' | 'cp437' | 'cp858';
}

const utf8Encoder = new TextEncoder();

const CODE_PAGE_NUMBER: Record<'cp437' | 'cp858', number> = {
  cp437: 0,
  cp858: 19,
};

const TEXT_NORMALIZATION_MAP: Record<string, string> = {
  '\u00A0': ' ',
  '\u202F': ' ',
  '\u2007': ' ',
  '\u2009': ' ',
  '\u200A': ' ',
  '\u200B': '',
  '\u2010': '-',
  '\u2011': '-',
  '\u2012': '-',
  '\u2013': '-',
  '\u2014': '-',
  '\u2212': '-',
  '\u2018': "'",
  '\u2019': "'",
  '\u201A': "'",
  '\u201C': '"',
  '\u201D': '"',
  '\u201E': '"',
  '\u2026': '...',
  '\u25CF': '*',
};

const normalizeText = (text: string): string => {
  let result = '';
  for (const ch of text) {
    result += TEXT_NORMALIZATION_MAP[ch] ?? ch;
  }
  return result;
};

 //Spanish/Latin POS code page
const CP858_MAP: Record<string, number> = {
  'Ç': 0x80, 'ü': 0x81, 'é': 0x82, 'â': 0x83, 'ä': 0x84, 'à': 0x85, 'å': 0x86, 'ç': 0x87,
  'ê': 0x88, 'ë': 0x89, 'è': 0x8A, 'ï': 0x8B, 'î': 0x8C, 'ì': 0x8D, 'Ä': 0x8E, 'Å': 0x8F,
  'É': 0x90, 'æ': 0x91, 'Æ': 0x92, 'ô': 0x93, 'ö': 0x94, 'ò': 0x95, 'û': 0x96, 'ù': 0x97,
  'ÿ': 0x98, 'Ö': 0x99, 'Ü': 0x9A, 'ø': 0x9B, '£': 0x9C, 'Ø': 0x9D, '×': 0x9E, 'ƒ': 0x9F,
  'á': 0xA0, 'í': 0xA1, 'ó': 0xA2, 'ú': 0xA3, 'ñ': 0xA4, 'Ñ': 0xA5, 'ª': 0xA6, 'º': 0xA7,
  '¿': 0xA8, '®': 0xA9, '¬': 0xAA, '½': 0xAB, '¼': 0xAC, '¡': 0xAD, '«': 0xAE, '»': 0xAF,
  'Á': 0xB5, 'Â': 0xB6, 'À': 0xB7, '©': 0xB8, '¢': 0xBD, '¥': 0xBE,
  '₡': 0xBD,
  'ã': 0xC6, 'Ã': 0xC7,
  'ð': 0xD0, 'Ð': 0xD1, 'Ê': 0xD2, 'Ë': 0xD3, 'È': 0xD4, '€': 0xD5,
  'Í': 0xD6, 'Î': 0xD7, 'Ï': 0xD8,
  'Ó': 0xE0, 'ß': 0xE1, 'Ô': 0xE2, 'Ò': 0xE3, 'õ': 0xE4, 'Õ': 0xE5,
  'µ': 0xE6, 'þ': 0xE7, 'Þ': 0xE8, 'Ú': 0xE9, 'Û': 0xEA, 'Ù': 0xEB,
  'ý': 0xEC, 'Ý': 0xED, '¯': 0xEE, '´': 0xEF,
  '±': 0xF1, '¾': 0xF3, '¶': 0xF4, '§': 0xF5, '÷': 0xF6, '¸': 0xF7,
  '°': 0xF8, '¨': 0xF9, '·': 0xFA, '¹': 0xFB, '³': 0xFC, '²': 0xFD,
};

const CP437_MAP: Record<string, number> = {
  'Ç': 0x80, 'ü': 0x81, 'é': 0x82, 'â': 0x83, 'ä': 0x84, 'à': 0x85, 'å': 0x86, 'ç': 0x87,
  'ê': 0x88, 'ë': 0x89, 'è': 0x8A, 'ï': 0x8B, 'î': 0x8C, 'ì': 0x8D, 'Ä': 0x8E, 'Å': 0x8F,
  'É': 0x90, 'æ': 0x91, 'Æ': 0x92, 'ô': 0x93, 'ö': 0x94, 'ò': 0x95, 'û': 0x96, 'ù': 0x97,
  'ÿ': 0x98, 'Ö': 0x99, 'Ü': 0x9A, '¢': 0x9B, '£': 0x9C, '¥': 0x9D, 'ƒ': 0x9F,
  '₡': 0x9B,
  'á': 0xA0, 'í': 0xA1, 'ó': 0xA2, 'ú': 0xA3, 'ñ': 0xA4, 'Ñ': 0xA5, 'ª': 0xA6, 'º': 0xA7,
  '¿': 0xA8, '¬': 0xAA, '½': 0xAB, '¼': 0xAC, '¡': 0xAD, '«': 0xAE, '»': 0xAF,
  'ß': 0xE1, 'µ': 0xE6, '°': 0xF8, '·': 0xFA, '²': 0xFD,
  '±': 0xF1, '÷': 0xF6,
};

const ASCII_FALLBACK_MAP: Record<string, string> = {
  'Á': 'A', 'É': 'E', 'Í': 'I', 'Ó': 'O', 'Ú': 'U',
  'Â': 'A', 'Ê': 'E', 'Î': 'I', 'Ô': 'O', 'Û': 'U',
  'À': 'A', 'È': 'E', 'Ì': 'I', 'Ò': 'O', 'Ù': 'U',
  'Ã': 'A', 'Õ': 'O',
  'á': 'a', 'é': 'e', 'í': 'i', 'ó': 'o', 'ú': 'u',
  '€': 'EUR',
  '¢': 'c',
};

const encodeWithMap = (text: string, map: Record<string, number>): number[] => {
  const out: number[] = [];
  const normalized = normalizeText(text);
  for (const ch of normalized) {
    const code = ch.codePointAt(0) ?? 0x3f;
    if (code < 0x80) {
      out.push(code);
      continue;
    }
    const mapped = map[ch];
    if (mapped !== undefined) {
      out.push(mapped);
      continue;
    }
    const fallback = ASCII_FALLBACK_MAP[ch];
    if (fallback !== undefined) {
      for (let i = 0; i < fallback.length; i++) {
        out.push(fallback.charCodeAt(i));
      }
      continue;
    }
    out.push(0x3f);
  }
  return out;
};

const encodeText = (text: string, codePage: EscPosEncodeOptions['codePage']): number[] => {
  if (codePage === 'utf8') {
    return Array.from(utf8Encoder.encode(text));
  }
  if (codePage === 'cp437') {
    return encodeWithMap(text, CP437_MAP);
  }
  return encodeWithMap(text, CP858_MAP);
};

export function uint8ToBase64(bytes: Uint8Array): string {
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    const sub = bytes.subarray(i, i + chunk);
    binary += String.fromCharCode(...sub);
  }
  return btoa(binary);
}

/**
 * Maps existing Epson-style template commands to ESC/POS bytes.
 * Used for generic ESC/POS (Electron + Android).
 */
const ESCPOS_FEED_LINES_BEFORE_CUT = 3;

export function encodeTemplateToEscPos(
  template: PrinterTemplate,
  options: EscPosEncodeOptions = {}
): Uint8Array {
  const chunks: number[] = [];
  const isDrawerKick = template.metadata.type === OPEN_CASH_DRAWER_TEMPLATE_TYPE;
  const { openCashDrawer = false, addFinalCut = !isDrawerKick, codePage = 'cp858' } = options;

  const push = (...b: number[]) => chunks.push(...b);

  push(ESC, 0x40); // Initialize

  if (codePage !== 'utf8') {
    push(ESC, 0x74, CODE_PAGE_NUMBER[codePage]);
  }

  for (const cmd of template.commands) {
    switch (cmd.method) {
      case 'addTextAlign': {
        const a = String(cmd.params[0] ?? 'left');
        if (a === 'center') push(ESC, 0x61, 1);
        else if (a === 'right') push(ESC, 0x61, 2);
        else push(ESC, 0x61, 0);
        break;
      }
      case 'addTextSize': {
        const w = Math.min(8, Math.max(1, Number(cmd.params[0]) || 1));
        const h = Math.min(8, Math.max(1, Number(cmd.params[1]) || 1));
        push(GS, 0x21, (w - 1) << 4 | (h - 1));
        break;
      }
      case 'addTextFont': {
        const f = String(cmd.params[0] ?? 'font_a');
        push(ESC, 0x4d, f === 'font_b' ? 1 : 0);
        break;
      }
      case 'addText': {
        const s = String(cmd.params[0] ?? '');
        push(...encodeText(s, codePage));
        break;
      }
      case 'addFeedLine': {
        const n = Math.max(1, Number(cmd.params[0]) || 1);
        for (let i = 0; i < n; i++) push(0x0a);
        break;
      }
      case 'addPulse':
        push(ESC, 0x70, 0, 60, 120);
        break;
      case 'addCut':
        push(GS, 0x56, 0x00);
        break;
      default:
        break;
    }
  }

  if (addFinalCut) {
    for (let i = 0; i < ESCPOS_FEED_LINES_BEFORE_CUT; i++) push(0x0a);
    push(GS, 0x56, 0x00);
  }
  if (openCashDrawer && !isDrawerKick) {
    push(ESC, 0x70, 0, 25, 250);
  }

  return new Uint8Array(chunks);
}
